/**
 * SetNewPasswordScreen — de landingspagina van de herstellink
 *
 * Dit scherm bestond niet. Wie op de link in de wachtwoordherstel-mail klikte,
 * kwam op de gewone app terecht met tokens in de adresbalk die niets deden: de
 * app boot normaal op en negeert de hash. Er was dus een compléte helft van de
 * herstelflow afwezig — de mail werd verstuurd, maar er was geen plek om een
 * nieuw wachtwoord te zetten.
 *
 * WAAROM DIT GEEN ROUTE IS
 *
 * Deze app heeft geen URL-router (bewust uitgesteld; `useState<NavView>`).
 * Een pad als `/wachtwoord-herstellen` zou dus nergens op uitkomen. De tokens
 * staan gelukkig in de HASH, en die is er ongeacht het pad — dus de detectie
 * hangt aan de hash en niet aan een route. Dat werkt ook op een statische host
 * die alleen `/` serveert.
 */

import React, { useEffect, useState } from 'react';
import { KeyRound, ShieldCheck, AlertTriangle } from 'lucide-react';
import { completePasswordReset } from '../../data/supabase/auth';
import { clearRecoveryHash, getRecoveryLinkError } from '../logic/recoveryLink';
import { PASSWORD_MIN_LENGTH } from '../../config/constants';

// De herstellink per e-mail bestaat alleen voor volwassen accounts — een leerling
// heeft geen echt e-mailadres en gebruikt de herstelCODE. Dus de eis voor
// volwassenen, niet het lagere leerlingminimum.
const MIN_LENGTH = PASSWORD_MIN_LENGTH.default;
interface SetNewPasswordScreenProps {
  /** Naar het inlogscherm, nadat het wachtwoord is gezet of de link dood blijkt. */
  onDone: () => void;
}
export function SetNewPasswordScreen({
  onDone
}: SetNewPasswordScreenProps) {
  const linkError = getRecoveryLinkError();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Tokens uit de adresbalk halen zodra dit scherm staat. Ze komen anders in de
  // browsergeschiedenis terecht, en in elke link die de gebruiker hierna deelt.
  useEffect(() => {
    clearRecoveryHash();
  }, []);
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = repeat.length > 0 && password !== repeat;
  const canSubmit = password.length >= MIN_LENGTH && !mismatch && repeat.length > 0 && !busy;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const result = await completePasswordReset(password);
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? 'Het wachtwoord is niet gewijzigd.');
      return;
    }
    setDone(true);
  };

  // ── Link verlopen of al gebruikt ─────────────────────────────────────────────
  // Dit is geen foutmelding maar een uitleg: er is niets misgegaan, de link is
  // gewoon oud. Zonder dit scherm zou de gebruiker op het inlogscherm landen en
  // dezelfde dode link nog eens proberen.
  if (linkError) {
    return <Frame data-id="element-545" data-mp="1fbq5mel04pj7">
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 p-4" data-id="element-546" data-mp="1fb6pn3ewwb7t">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-547" data-mp="1ad55sg9nw7va" />
          <div data-id="element-548" data-mp="1ocoaez39l12u">
            <h1 className="text-base font-bold text-amber-900" data-id="element-549" data-mp="1od68m19hlpde">
              Deze link werkt niet meer
            </h1>
            <p className="mt-1 text-sm leading-relaxed text-amber-900" data-id="element-550" data-mp="1ka0716xbkmd9">{linkError}</p>
          </div>
        </div>
        <button onClick={onDone} className="mt-5 w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800" data-id="element-551" data-mp="1y99la11yt6el">
          Naar inloggen
        </button>
      </Frame>;
  }

  // ── Gelukt ───────────────────────────────────────────────────────────────────
  if (done) {
    return <Frame data-id="element-552" data-mp="233ni87w9zlld">
        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 p-4" data-id="element-553" data-mp="2h1brsjkw76b4">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" data-id="element-554" data-mp="2gz944677pp9k" />
          <div data-id="element-555" data-mp="2c423565buyoc">
            <h1 className="text-base font-bold text-emerald-900" data-id="element-556" data-mp="2c0ebydncuocs">
              Je wachtwoord is gewijzigd
            </h1>
            <p className="mt-1 text-sm leading-relaxed text-emerald-900" data-id="element-557" data-mp="fbmp76afyyb3">
              Je bent nu ingelogd op dit apparaat. Op je andere apparaten moet je
              opnieuw inloggen met je nieuwe wachtwoord.
            </p>
          </div>
        </div>
        <button onClick={onDone} className="mt-5 w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800" data-id="element-558" data-mp="o6km0ogpmv7p">
          Verder
        </button>
      </Frame>;
  }

  // ── Nieuw wachtwoord zetten ──────────────────────────────────────────────────
  return <Frame data-id="element-559" data-mp="dknl1mz0mwh">
      <div className="flex items-start gap-3" data-id="element-560" data-mp="h8gbykld5a5">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50" data-id="element-561" data-mp="5f1jsjuifl6q">
          <KeyRound className="h-4 w-4 text-indigo-600" aria-hidden="true" data-id="element-562" data-mp="1bt97fk4xh5t2" />
        </span>
        <div data-id="element-563" data-mp="1gs415h4foery">
          <h1 className="text-lg font-bold text-slate-900" data-id="element-564" data-mp="1grucpxz1efod">
            Kies een nieuw wachtwoord
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-565" data-mp="1qwanicxnt6lb">
            Je bent via de link uit je e-mail gekomen. Zodra je het nieuwe
            wachtwoord opslaat, ben je ingelogd.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-id="element-566" data-mp="1d5noejnzleop">
        <label className="block" data-id="element-567" data-mp="1d5fbirrv69di">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-568" data-mp="186kr5le90cra">
            Nieuw wachtwoord
          </span>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" autoFocus className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" data-id="element-569" data-mp="136gbfxl5zm24" />
        </label>
        {tooShort && <p className="text-xs leading-relaxed text-amber-700" data-id="element-570" data-mp="2slw9agtn1lcp">
            Gebruik minstens {MIN_LENGTH} tekens.
          </p>}

        <label className="block" data-id="element-571" data-mp="2oizxpa2impi9">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-572" data-mp="lxnlx6gby7lw">
            Nog een keer
          </span>
          <input type="password" value={repeat} onChange={e => setRepeat(e.target.value)} autoComplete="new-password" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" data-id="element-573" data-mp="qpnoz7rp5gmp" />
        </label>
        {mismatch && <p className="text-xs text-amber-700" data-id="element-574" data-mp="14p6lt4f255jp">
            De twee wachtwoorden zijn niet gelijk.
          </p>}

        {error && <p role="alert" className="rounded-xl bg-rose-50 p-3 text-sm leading-relaxed text-rose-900" data-id="element-575" data-mp="19lnhcjol6m6t">
            {error}
          </p>}

        <button type="submit" disabled={!canSubmit} className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-576" data-mp="10mjy9fpf66sb">
          {busy ? 'Bezig…' : 'Wachtwoord opslaan'}
        </button>
      </form>

      <button onClick={onDone} className="mt-3 w-full rounded-xl px-4 py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-50" data-id="element-577" data-mp="3cviikkcl927o">
        Annuleren
      </button>
    </Frame>;
}
function Frame({
  children
}: {
  children: React.ReactNode;
}) {
  return <main className="flex min-h-screen w-full items-center justify-center bg-slate-50 p-4" data-id="element-578" data-mp="3hvd9gnwd5rsq">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-579" data-mp="3htk2f7hu95k7">
        {children}
      </div>
    </main>;
}